import { c as _c } from "react/compiler-runtime";
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useEagGridStore } from '../store/useEagGridStore';
import type { Filters } from '../types/Filters';
import { getPageFromUrl, setPageInUrl } from '../utils/url/getSearchParamsByGridCode';
type UsePageUrlSyncParams = {
  gridCode: string;
  page: number;
  filterValues: Filters;
  queryId: string;
};

/**
 * Owns the full page lifecycle for a single EagGrid instance:
 *
 * - **Phase 1 (mount):** reads page from `&page=gridCode:N` URL param and initialises the store
 *   (converts 1-based URL to 0-based store). Runs as `useLayoutEffect` so the store is updated
 *   before the fetch effect sees `offset`.
 *
 * - **Phase 2 (page change):** writes the current page to `&page=gridCode:N` via `replaceState`.
 *   Reads `store.getState().page` (not `params.page`) to avoid writing the stale React snapshot
 *   on mount.
 *
 * - **Phase 3 (filter/preset change):** resets page to 0 when `filterValues` or `queryId` changes
 *   after the grid has fully hydrated. Hydration is detected semantically: the grid is considered
 *   hydrated once `queryId` transitions from `''` to a real value (set by `useServerData.fetchPage`
 *   after the first data fetch). Until that point, all changes are initialization and must not
 *   reset the page. The `Boolean(prevQueryIdRef.current)` guard prevents the initial `'' → 'abc-123'`
 *   queryId transition from being treated as a preset switch.
 *
 *   This approach is immune to React 18 StrictMode double-invocation, extra re-renders, or any
 *   other scheduling variation — it tracks semantic readiness, not effect execution count.
 *
 * ## URL format
 * Page is stored as a separate top-level query param (not inside `dataGrids`):
 *   `?dataGrids=vehicle-opensearch,abc,1|&page=vehicle-opensearch:3`
 * Multiple grids:
 *   `?dataGrids=vehicle-opensearch,abc,1|customers,def,2|&page=vehicle-opensearch:3,customers:1`
 *
 * ## Why useLayoutEffect for Phase 1
 * Phase 1 MUST run as `useLayoutEffect` (not `useEffect`/`useOnMount`).
 * EagGridTable's data-fetching effect (`useEffect([offset, ...])`) uses `offset`
 * derived from `page`. If we restored page in a regular `useEffect`, the fetch
 * would run first with `offset=0` (wrong), triggering a wasted HTTP request.
 *
 * `useLayoutEffect` runs synchronously before any `useEffect`. When `setPage`
 * mutates the store, `useSyncExternalStore` triggers a synchronous re-render so
 * the fetch effect sees the correct offset immediately — single fetch on mount.
 */
export const usePageUrlSync = params => {
  const $ = _c(16);
  const store = useEagGridStore();
  const initializedRef = useRef(false);
  let t0;
  let t1;
  if ($[0] !== params.gridCode || $[1] !== store) {
    t0 = () => {
      if (initializedRef.current) {
        return;
      }
      const urlPage = getPageFromUrl(params.gridCode);
      if (urlPage !== undefined && urlPage >= 1) {
        store.setPage(urlPage - 1);
      }
      initializedRef.current = true;
    };
    t1 = [params.gridCode, store];
    $[0] = params.gridCode;
    $[1] = store;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useLayoutEffect(t0, t1);
  let t2;
  if ($[4] !== params.gridCode || $[5] !== store) {
    t2 = () => {
      if (!initializedRef.current) {
        return;
      }
      setPageInUrl(params.gridCode, store.getState().page + 1);
    };
    $[4] = params.gridCode;
    $[5] = store;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== params.gridCode || $[8] !== params.page || $[9] !== store) {
    t3 = [params.page, params.gridCode, store];
    $[7] = params.gridCode;
    $[8] = params.page;
    $[9] = store;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  useEffect(t2, t3);
  const hydratedRef = useRef(false);
  const prevFilterRef = useRef(params.filterValues);
  const prevQueryIdRef = useRef(params.queryId);
  let t4;
  let t5;
  if ($[11] !== params.filterValues || $[12] !== params.queryId || $[13] !== store) {
    t4 = () => {
      const filtersChanged = prevFilterRef.current !== params.filterValues;
      const presetChanged = prevQueryIdRef.current !== params.queryId && Boolean(prevQueryIdRef.current);
      if (!hydratedRef.current) {
        if (params.queryId !== "" && prevQueryIdRef.current === "") {
          hydratedRef.current = true;
        }
        prevFilterRef.current = params.filterValues;
        prevQueryIdRef.current = params.queryId;
        return;
      }
      if (filtersChanged || presetChanged) {
        store.setPage(0);
      }
      prevFilterRef.current = params.filterValues;
      prevQueryIdRef.current = params.queryId;
    };
    t5 = [params.filterValues, params.queryId, store];
    $[11] = params.filterValues;
    $[12] = params.queryId;
    $[13] = store;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  useEffect(t4, t5);
};